import { createClient, createCache } from 'react-fetching-library';
import { setIsLogin } from 'redux_store';
import showServerError from '@shared/lib/showError';
import parsePermissionSafeJson from '@shared/lib/parsePermissionSafeJson';
import { recordSessionHeaders } from '@shared/lib/idleSession';
import { notifyAuthenticatedResponse } from '@shared/lib/authenticatedResponse';
import i18nInitialState from './translations';

// react-fetching-library parses bodies via response.json(), which
// corrupts permission masks above 2^53 (see parsePermissionSafeJson —
// the account-switch and HIPAA-check payloads carry authMetadata with
// the same mask fields). Wrap fetch so json() routes through the
// precision-safe parser instead.
const permissionSafeFetch = async (input, init) => {
  const response = await window.fetch(input, init);
  const readText = response.text.bind(response);
  response.json = async () => parsePermissionSafeJson(await readText());
  return response;
};

const requestInterceptor = host => () => async action => {
  const headers = {
    ...action.headers,
    'Authorization-SetAsCookie': 'true',
    Client: 'dashboard',
    'Cache-Control': 'no-store, no-cache, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
    Accept: 'application/json',
    'Accept-Charset': 'utf-8',
  };

  // Add Content-Type for requests with body (but not for FormData)
  if (
    (action.method === 'POST' || action.method === 'PUT' || action.method === 'PATCH') &&
    action.body
  ) {
    // Don't set Content-Type for FormData - let browser set it with boundary
    if (!(action.body instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json; charset=utf-8';
    }
  }

  return {
    ...action,
    headers,
    credentials: 'include',
    endpoint: `${host}${action.endpoint}`,
  };
};

const responseInterceptor = dispatch => () => async (action, response) => {
  // An authenticated response (session headers present) is the sign the
  // user is signed in; the headers themselves feed features/session-idle.
  const session = recordSessionHeaders(response.headers);
  if (session.authenticated && dispatch) {
    dispatch(setIsLogin(true));
  }
  // A successful authenticated request: the alerts hub may (re)start.
  if (session.authenticated && response.status < 400) notifyAuthenticatedResponse();

  if (response.payload.data !== undefined) {
    return {
      ...response,
      payload: response.payload.data,
    };
  }

  if (response.status === 401 || response.status === 403) {
    // Actions may opt out of the toast for 403 with
    // `suppress403Toast: true` — same action-level escape hatch as
    // `skipCache` below. The permissions-testing GET endpoints use it:
    // that feature answers 404 where it is switched off and treats 403
    // the same way, rendering one "not available here" line, so a
    // second, contradictory "no permissions" snackbar next to it is
    // noise. 401 is never silenced: it means the session expired, and
    // that has to be visible on every page.
    if (!(action.suppress403Toast && response.status === 403)) {
      showServerError(dispatch, i18nInitialState.en.notifications.noPermissions);
    }
    return { ...response, payload: null, error: true };
  }

  if (response.status === 410) {
    window.location.href = '/login#logout';
    return;
  }

  return response;
};

const CACHE_TIME = 3000;

// Short GET cache to dedupe bursts of identical metadata reads. Actions
// may opt out with `skipCache: true` — table list queries do (see
// useFetchList): after a row mutation (no-show, resend, delete...) the
// list is re-fetched ~600 ms later, and a cache hit from a list GET made
// < 3 s earlier would serve the PRE-mutation rows, so the table never
// visibly updated (repro: set no-show, then reverse it within 3 s).
const cache = createCache(
  action => action.method === 'GET' && !action.skipCache,
  response => new Date().getTime() - response.timestamp < CACHE_TIME
);

const client = dispatch =>
  createClient({
    requestInterceptors: [requestInterceptor(process.env.REACT_APP_API_URL)],
    responseInterceptors: [responseInterceptor(dispatch)],
    cacheProvider: cache,
    fetch: permissionSafeFetch,
  });

export default client;
